import * as Api from '@ViewModels';
import { Button } from '@HoudiniUI/Button';
import { Input } from '@HoudiniUI/Input';
import { Label } from '@HoudiniUI/Label';
import { THEME } from '@HoudiniUI/Theme/theme';
import { css } from 'aphrodite';
import * as React from 'react';
import { EditItemPenIcon } from '../../../../svgs/icons/EditItemPenIcon';
import { TrashIcon } from '../../../../svgs/icons/TrashIcon';
import { styleSheet } from './styles';

export type VerifyStatus = 'idle' | 'validating' | 'success' | 'error';

interface IProps {
	mailbox: Api.ISharedMailbox;
	isMicrosoft: boolean;
	isEditing: boolean;
	verifyStatus: VerifyStatus;
	verifyMessage: string | null;
	saveError: string | null;
	isSaving: boolean;
	isRemoving: boolean;
	testDisabled: boolean;
	onStartEdit: () => void;
	onCancelEdit: () => void;
	onSave: (values: { emailAddress: string; name?: string }) => void;
	onRemove: () => void;
	onTest: () => void;
}

export const SharedMailboxRow: React.FC<IProps> = ({
	mailbox,
	isMicrosoft,
	isEditing,
	verifyStatus,
	verifyMessage,
	saveError,
	isSaving,
	isRemoving,
	testDisabled,
	onStartEdit,
	onCancelEdit,
	onSave,
	onRemove,
	onTest,
}) => {
	const [emailAddress, setEmailAddress] = React.useState(mailbox.emailAddress);
	const [name, setName] = React.useState(mailbox.name ?? '');

	// Seed the fields only when edit mode starts; the collection polls while a test runs, and a mid-edit
	// server update must not clobber what the user is typing.
	const wasEditingRef = React.useRef(false);
	React.useEffect(() => {
		if (isEditing && !wasEditingRef.current) {
			setEmailAddress(mailbox.emailAddress);
			setName(mailbox.name ?? '');
		}
		wasEditingRef.current = isEditing;
	}, [isEditing, mailbox.emailAddress, mailbox.name]);

	const isDirty = emailAddress.trim() !== mailbox.emailAddress || (name.trim() || '') !== (mailbox.name ?? '');
	const addressFieldId = `sharedMailboxAddress-${mailbox.id}`;
	const nameFieldId = `sharedMailboxName-${mailbox.id}`;
	const isValidating = verifyStatus === 'validating';

	return (
		<div className={css(styleSheet.mailboxRow)}>
			{isEditing ? (
				<>
					<div className={css(styleSheet.field)}>
						<Label htmlFor={addressFieldId} styles={[styleSheet.fieldLabel]}>
							Address
						</Label>
						<Input
							id={addressFieldId}
							type='email'
							placeholder='team@company.com'
							value={emailAddress}
							onChange={e => setEmailAddress(e.target.value)}
							styles={[styleSheet.inputControl]}
						/>
					</div>
					<div className={css(styleSheet.field)}>
						<Label htmlFor={nameFieldId} styles={[styleSheet.fieldLabel]}>
							Display name (optional)
						</Label>
						<Input
							id={nameFieldId}
							type='text'
							placeholder='Team Inbox'
							value={name}
							onChange={e => setName(e.target.value)}
							styles={[styleSheet.inputControl]}
						/>
					</div>
					{!isMicrosoft ? (
						<div className={css(styleSheet.gmailHint)}>
							The address must be configured as a “send-as” alias in your Google account.
						</div>
					) : null}
					{saveError ? <div className={css(styleSheet.validationError)}>{saveError}</div> : null}
					<div className={css(styleSheet.actions)}>
						<Button
							variant='primary'
							size='sm'
							disabled={!isDirty}
							isLoading={isSaving}
							onClick={() => onSave({ emailAddress: emailAddress.trim(), name: name.trim() || undefined })}
							styles={[styleSheet.actionButton]}
						>
							Save
						</Button>
						<Button variant='secondary' size='sm' onClick={onCancelEdit} styles={[styleSheet.actionButton]}>
							Cancel
						</Button>
					</div>
				</>
			) : (
				<div className={css(styleSheet.rowSummary)}>
					<div className={css(styleSheet.rowIdentity)}>
						<span className={css(styleSheet.rowName)}>{mailbox.name || mailbox.emailAddress}</span>
						{mailbox.name ? <span className={css(styleSheet.rowAddress)}>{mailbox.emailAddress}</span> : null}
					</div>
					{isValidating ? (
						<span className={css(styleSheet.rowBadge, styleSheet.verifyValidating)}>Validating…</span>
					) : mailbox.verified ? (
						<span className={css(styleSheet.rowBadge, styleSheet.verifySuccess)}>✓ Verified</span>
					) : (
						<span className={css(styleSheet.rowBadge, styleSheet.rowBadgeWarning)}>Not verified yet</span>
					)}
					<div className={css(styleSheet.rowActions)}>
						{!mailbox.verified && !isValidating ? (
							<Button
								variant='secondary'
								size='sm'
								disabled={testDisabled}
								onClick={onTest}
								styles={[styleSheet.actionButton]}
							>
								Test
							</Button>
						) : null}
						<Button variant='ghost' size='icon' disabled={isRemoving} onClick={onStartEdit}>
							<span className={css(styleSheet.srOnly)}>Edit shared mailbox</span>
							<EditItemPenIcon fillColor={THEME.colors.icon.gray} />
						</Button>
						<Button variant='ghost' size='icon' isLoading={isRemoving} onClick={onRemove}>
							<span className={css(styleSheet.srOnly)}>Remove shared mailbox</span>
							<TrashIcon />
						</Button>
					</div>
				</div>
			)}

			{verifyStatus === 'validating' ? (
				<div className={css(styleSheet.verifyStatus, styleSheet.verifyValidating)}>Validating shared mailbox…</div>
			) : null}
			{verifyStatus === 'success' ? (
				<div className={css(styleSheet.verifyStatus, styleSheet.verifySuccess)}>
					✓ Verified: this mailbox can send. The test goes to a Levitate verification address (not your inbox), so you
					won’t receive a copy.
				</div>
			) : null}
			{verifyStatus === 'error' ? (
				<div className={css(styleSheet.verifyStatus, styleSheet.verifyError)}>
					<span>✗ {verifyMessage}</span>
					<Button variant='link' size='sm' disabled={testDisabled} onClick={onTest}>
						Try again
					</Button>
				</div>
			) : null}
		</div>
	);
};
